import { StrandedSite } from '@/types/site'
import {
  computeStrandedScore, scoreTierClass, scoreTierColor, scoreTier,
  effectiveGridKm, effectiveInternetFactor, hasMeasuredGrid, hasStrongConnectivity,
} from './scoring'
import { scorePercentile, scorePercentiles, scoreBadgeLabel } from './percentile'

export type EnrichedSite = StrandedSite & {
  id: string
  strandedScore: number
  scorePercentile?: number
  scoreBadge?: string
  potentialDailyProfitUsd: number // rough optimistic, USD @ $85k BTC scenario
  emission: number
  recommendedGenset?: keyof typeof GENSET_DATA
  maxGeneratorPowerKW?: number
}

export {
  computeStrandedScore, scorePercentile, scoreBadgeLabel, scoreTierClass, scoreTierColor, scoreTier,
  effectiveGridKm, effectiveInternetFactor, hasMeasuredGrid, hasStrongConnectivity,
}

export function enrichSite(site: StrandedSite): EnrichedSite {
  const p = site.properties
  const emission = p.emission_rate_kg_day || 0
  const score = computeStrandedScore(site)

  // Very rough optimistic daily profit potential using average machine assumptions.
  // Declared heuristic: USD at a fixed $85k BTC "optimistic scenario", after a 0.82
  // opex/net haircut. No FX conversion here (unit pure USD) — live-rate consumers
  // scale it by the live BTC price (see lib/portfolio.ts scalePotentialUsd).
  const roughDailyBtc = (emission / 22000) * 0.0008 // tuned heuristic from real emission -> usable methane energy
  const potentialDailyProfitUsd = Math.round(roughDailyBtc * 85000 * 0.82) // after power/opex (optimistic scenario)

  // Deterministic fallback — never random (breaks bookmarks/mission across reloads)
  const ghgrp = p.ghgrp_id || p.id || (() => {
    const [lng, lat] = site.geometry?.coordinates || [0, 0]
    const key = `${p.name || 'site'}|${p.province || ''}|${lng.toFixed(5)},${lat.toFixed(5)}`
    let h = 0
    for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0
    return `site-${(h >>> 0).toString(36)}`
  })()

  return {
    ...site,
    id: ghgrp,
    strandedScore: score,
    potentialDailyProfitUsd,
    emission,
    // Honesty: no reported CH₄ → no generator/ASIC recommendation (emission, hence
    // power, is unknown). Consumers must render the "not modelled" badge instead.
    recommendedGenset: emission > 0 ? recommendGenset(emission) : undefined,
    maxGeneratorPowerKW: emission > 0 ? Math.round(computeGeneratorPower(emission)) : undefined,
  }
}

/**
 * One site, from its own published record (`/data/site/<id>.json`, ~950 B; 587 B gzipped).
 *
 * The deep-link fast path. A shared link used to wait for the whole portfolio — 2.85 MB of
 * geojson to parse, 2,611 features to enrich and percentile-rank — before the named site's
 * card could appear; measured 10–37 s on a phone and provably CPU-bound. This returns the
 * SAME EnrichedSite `loadSites()` would produce for that site (generated from the same
 * canonical geojson by the same functions — see scripts/generate-site-records.mjs), so the
 * card the person sees first is the card they keep, only sooner. The dataset still loads
 * behind it and the map fills in as usual.
 *
 * Returns null when there is no record (older deploy, offline, unknown id) — callers must
 * fall back to the dataset path, never invent a site.
 */
/*
 * Fix 4/4 — warm-start the deep link. The card's record is tiny (~1 KB), but the fetch
 * used to START only after the whole page module had downloaded, parsed, and hydrated —
 * measured ~3.6 s into a slow-4G cold load. Memoizing the promise lets the page kick the
 * fetch off at module scope (overlapping boot with the request) and still dedupes any
 * later call. The record itself is unchanged; this only starts it sooner.
 */
const siteRecordCache = new Map<string, Promise<EnrichedSite | null>>()

export async function loadSiteRecord(id: string): Promise<EnrichedSite | null> {
  if (!id) return null
  const cached = siteRecordCache.get(id)
  if (cached) return cached
  const promise = (async () => {
    try {
      const res = await fetch(`/data/site/${encodeURIComponent(id)}.json`)
      if (!res.ok) return null
      const record = (await res.json()) as EnrichedSite
      if (!record?.id || !record.geometry?.coordinates?.length || !record.properties) return null
      return record
    } catch {
      return null
    }
  })()
  siteRecordCache.set(id, promise)
  return promise
}

export async function loadSites(): Promise<EnrichedSite[]> {
  let data: GeoJSON.FeatureCollection
  try {
    const res = await fetch('/data/stranded-sites.geojson')
    if (!res.ok) throw new Error('fetch failed')
    data = await res.json()
    if (typeof window !== 'undefined') {
      const { setCachedGeojson } = await import('./offline-db')
      await setCachedGeojson(data)
    }
  } catch {
    if (typeof window !== 'undefined') {
      const { getCachedGeojson } = await import('./offline-db')
      const cached = await getCachedGeojson()
      if (!cached?.features?.length) throw new Error('No site data available')
      data = cached
    } else {
      throw new Error('No site data available')
    }
  }
  const features: StrandedSite[] = (data.features || []) as StrandedSite[]
  const enriched = features.map(enrichSite)
  // One pass over the score distribution (not one sort per site — see scorePercentiles).
  const percentiles = scorePercentiles(enriched.map(s => s.strandedScore))
  return enriched.map((s, i) => ({
    ...s,
    scorePercentile: percentiles[i],
    scoreBadge: scoreBadgeLabel(percentiles[i]),
  }))
}

export function getProvinces(sites: EnrichedSite[]) {
  return Array.from(new Set(sites.map(s => s.properties.province).filter(Boolean))).sort()
}

// Shared real-world genset data for methane-to-power (approx 2026 biogas/stranded methane rated, from education page)
export const GENSET_DATA = {
  mobile250: { name: "Mobile 250 kW Biogas Unit", powerKW: 250, eff: 0.33, methaneNm3h: 85, capexPerKW: 1800, notes: "Fast deploy, H2S tolerant" },
  jenbacher316: { name: "INNIO Jenbacher J316 GS-B.L", powerKW: 850, eff: 0.405, methaneNm3h: 220, capexPerKW: 1050, notes: "High efficiency, gold standard" },
  cat3520: { name: "Caterpillar G3520H", powerKW: 2000, eff: 0.42, methaneNm3h: 480, capexPerKW: 920, notes: "High power density, rugged" },
  man: { name: "MAN 20V35/44G", powerKW: 10500, eff: 0.47, methaneNm3h: 2300, capexPerKW: 780, notes: "Large scale, highest efficiency" },
  cummins: { name: "Cummins QSK60G", powerKW: 1500, eff: 0.39, methaneNm3h: 390, capexPerKW: 1100, notes: "Balanced cost/performance" },
  microturbine: { name: "Capstone C200 Microturbine", powerKW: 200, eff: 0.33, methaneNm3h: 55, capexPerKW: 2100, notes: "Ultra-low maintenance" },
  wtsila: { name: "Wärtsilä 20V34SG", powerKW: 9500, eff: 0.46, methaneNm3h: 2100, capexPerKW: 750, notes: "High efficiency large scale" },
  futureSOFC: { name: "Solid Oxide Fuel Cell (2028+)", powerKW: 500, eff: 0.60, methaneNm3h: 90, capexPerKW: 3500, notes: "Future high-eff, low emission" },
} as const

export type GensetId = keyof typeof GENSET_DATA

/**
 * Nm³ of CH₄ per DAY → average electrical kW.
 *
 * The ÷24 is the whole point: Nm³/day ÷ Nm³/hour yields full-power HOURS PER
 * DAY, and kW × hours = kWh (daily ENERGY), not power. Dividing by 24 turns
 * that daily energy into an average power. Omitting it inflates every
 * downstream figure — generator kW, miner ceiling, sats/day, capex — by 24×.
 * Aggregate genset-hours can exceed 24: this helper is gas-equivalent fleet
 * potential, NOT one installed machine. Use dispatchSiteGas for an inventory cap.
 * Route every methane→power conversion through here (or computeGeneratorPower).
 */
export function methaneNm3DayToKw(
  nm3PerDay: number,
  gensetId: GensetId = 'jenbacher316',
  derate = 0.9,
): number {
  const g = GENSET_DATA[gensetId]
  if (!g || !(g.methaneNm3h > 0) || !(nm3PerDay > 0)) return 0
  return ((nm3PerDay / g.methaneNm3h) * g.powerKW * derate) / 24
}

// Compute generator power (average kW) from daily methane kg using genset
export function computeGeneratorPower(dailyMethaneKg: number, gensetId: GensetId = 'jenbacher316', derate = 0.9): number {
  const dailyM3 = dailyMethaneKg / 0.717 // approx kg CH4 to Nm3
  return methaneNm3DayToKw(dailyM3, gensetId, derate)
}

// Recommend genset based on emission (larger for bigger sites)
export function recommendGenset(emissionKgDay: number): GensetId {
  if (emissionKgDay > 20000) return 'man'
  if (emissionKgDay > 10000) return 'cat3520'
  if (emissionKgDay > 5000) return 'jenbacher316'
  return 'mobile250'
}

// Compute full per-site Value including generator CapEx + mining (simplified from panel)
export function computeSiteValue(site: EnrichedSite, gensetId: GensetId = 'jenbacher316', asicHashrate = 200, asicPowerW = 3500, asicCost = 5500, liveBtc = 85000, uptime = 0.95, powerPriceUsdKwh = 0.04) {
  const emission = site.emission || 0
  const powerKW = computeGeneratorPower(emission, gensetId)
  const numAsics = Math.max(1, Math.floor(powerKW * 1000 / asicPowerW))
  // NOTE(honesty): 0.0000009 BTC/TH/day is an OPTIMISTIC scenario (~2.2× the network-derived
  // ~0.00000041 BTC/TH/day). Kept as default (published paybacks must not shift), surfaced as
  // "optimistic scenario" in the panel, methodology, education and exports.
  // Price production once (fleet-model pattern): BTC-denominated hashprice; price converts
  // revenue exactly once below — never scales the physical production term.
  const dailyBtc = numAsics * asicHashrate * 0.0000009 // adjusted
  const gensetCapex = GENSET_DATA[gensetId].powerKW * GENSET_DATA[gensetId].capexPerKW
  const miningCapex = numAsics * asicCost
  const totalCapex = gensetCapex + miningCapex
  // Power price is USD/kWh and revenue is USD — unit pure, no FX factor mixed in.
  const dailyOpexUsd = (powerKW * powerPriceUsdKwh * 24) // power + maint
  const dailyProfitUsd = (dailyBtc * liveBtc) - dailyOpexUsd
  const annualProfit = dailyProfitUsd * 365 * uptime
  const paybackDays = annualProfit > 0 ? totalCapex / (dailyProfitUsd * uptime) : Infinity
  const methaneLossDailyBtc = dailyBtc // opportunity cost of venting
  return {
    powerKW: Math.round(powerKW),
    numAsics,
    dailyBtc: +dailyBtc.toFixed(4),
    totalCapex: Math.round(totalCapex),
    paybackDays: isFinite(paybackDays) ? Math.round(paybackDays) : Infinity,
    methaneLossDailyBtc: +methaneLossDailyBtc.toFixed(4),
    gensetName: GENSET_DATA[gensetId].name,
  }
}

export function filterSites(
  sites: EnrichedSite[],
  opts: {
    minEmission?: number
    maxEmission?: number
    provinces?: Set<string>
    sourceTypes?: Set<string>
    minScore?: number
  }
) {
  return sites.filter(s => {
    const p = s.properties
    const em = s.emission
    if (opts.minEmission != null && em < opts.minEmission) return false
    if (opts.maxEmission != null && em > opts.maxEmission) return false
    if (opts.provinces?.size && !opts.provinces.has(p.province)) return false
    if (opts.sourceTypes?.size && !opts.sourceTypes.has(p.source_type)) return false
    if (opts.minScore != null && s.strandedScore < opts.minScore) return false
    return true
  })
}
